/**
 * Driver-portal API client.
 *
 * Wraps axios with a single response/request interceptor pair that:
 *   - attaches the access JWT on each outbound call
 *   - on 401, attempts ONE refresh-token rotation and replays the original
 *     request transparently
 *   - on refresh failure, clears tokens and lets the router redirect to /login
 *
 * Token storage is localStorage. Drivers redeem on a mobile browser they
 * own — sessionStorage would force re-auth on every tab close, which is
 * the wrong UX trade-off for this audience. The blast radius of a stolen
 * device is already constrained by short access lifetime + single-use
 * refresh rotation.
 */
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { currentSurface } from '@/config/hosts'

const ACCESS_KEY = 'cs_driver_access_token'
const REFRESH_KEY = 'cs_driver_refresh_token'
const EMPLOYEE_KEY = 'cs_driver_employee_id'
export const DRIVER_OIDC = import.meta.env.VITE_DRIVER_OIDC === '1'
let csrfToken: string | null = null
const CARRIER_KEY = 'cs_driver_carrier_id'
let selectedCarrierId: string | null = null

export interface DriverCarrier {
  carrier_id: string
  carrier_name: string
  role: 'driver' | 'mechanic'
}

export function getDriverCarrierId(): string | null {
  if (!DRIVER_OIDC) return null
  if (selectedCarrierId) return selectedCarrierId
  try { return sessionStorage.getItem(CARRIER_KEY) } catch { return null }
}

export function setDriverCarrierId(carrierId: string | null): void {
  selectedCarrierId = carrierId
  try {
    if (carrierId) sessionStorage.setItem(CARRIER_KEY, carrierId)
    else sessionStorage.removeItem(CARRIER_KEY)
  } catch { /* The current tab still keeps the choice in memory. */ }
}

export async function oidcCarriers(): Promise<DriverCarrier[]> {
  const response = await axios.get<DriverCarrier[]>('/api/v1/driver/carriers', {
    withCredentials: true,
  })
  return response.data
}

export function notifyCarrierRequired(error: AxiosError): void {
  if (DRIVER_OIDC && error.response?.status === 409 &&
      (error.response.data as { detail?: string } | undefined)?.detail === 'carrier_required') {
    window.dispatchEvent(new Event('driver:carrier-required'))
  }
}

export function driverRequestHeaders(): Record<string, string> {
  if (!DRIVER_OIDC) return { Authorization: `Bearer ${tokenStorage.get() ?? ''}` }
  const headers: Record<string, string> = { 'X-CSRF-Token': csrfToken ?? '' }
  const carrierId = getDriverCarrierId()
  if (carrierId) headers['X-Carrier-Id'] = carrierId
  return headers
}

function oidcSurface(): 'driver' | 'mechanic' {
  return currentSurface() === 'mechanic' ? 'mechanic' : 'driver'
}

export function startDriverSignIn(next?: string): void {
  const query = new URLSearchParams({ surface: oidcSurface() })
  if (next) query.set('next', next)
  window.location.assign(`/api/v1/oidc/driver/login?${query}`)
}

export async function oidcSession(): Promise<{ person_id: string; csrf_token: string }> {
  const response = await axios.get<{ person_id: string; csrf_token: string }>(
    '/api/v1/oidc/driver/session', { withCredentials: true },
  )
  csrfToken = response.data.csrf_token
  return response.data
}

export async function oidcLogout(): Promise<void> {
  let logoutUrl: string | undefined
  try {
    const { data } = await axios.post<{ logout_url?: string }>(`/api/v1/oidc/driver/logout?surface=${oidcSurface()}`, {}, {
      withCredentials: true, headers: { 'X-CSRF-Token': csrfToken ?? '' },
    })
    logoutUrl = data?.logout_url
  } finally {
    csrfToken = null
  }
  if (logoutUrl) window.location.assign(logoutUrl)
}

export interface TokenPair {
  access_token: string
  refresh_token: string
  employee_id: string
}

export interface LedgerEntry {
  id: number
  source_type: string
  source_ref: string
  points_delta: number
  metadata?: Record<string, unknown> | null
  created_at: string
}

export interface MeResponse {
  employee_id: string
  first_name: string | null
  last_name: string | null
  email: string | null
  balance: number
  program_enabled: boolean
  points_per_dollar: number
  min_redemption_points: number
  history: LedgerEntry[]
}

export interface DriverOrder {
  id: number
  external_id: string
  tremendous_order_id: string | null
  points_redeemed: number
  dollar_amount_cents: number
  product_id: string
  delivery_email: string
  status: string
  failure_reason: string | null
  refunded: boolean
  delivered_at: string | null
  created_at: string
}

export interface CatalogProduct {
  id: string
  name: string
  description: string | null
  category: string | null
}

export interface Catalog {
  products: CatalogProduct[]
  points_per_dollar: number
  min_redemption_points: number
  program_enabled: boolean
}

export const tokenStorage = {
  get(): string | null {
    if (DRIVER_OIDC) return null
    return localStorage.getItem(ACCESS_KEY)
  },
  getRefresh(): string | null {
    if (DRIVER_OIDC) return null
    return localStorage.getItem(REFRESH_KEY)
  },
  getEmployeeId(): string | null {
    if (DRIVER_OIDC) return null
    return localStorage.getItem(EMPLOYEE_KEY)
  },
  set(pair: TokenPair) {
    if (DRIVER_OIDC) return
    localStorage.setItem(ACCESS_KEY, pair.access_token)
    localStorage.setItem(REFRESH_KEY, pair.refresh_token)
    localStorage.setItem(EMPLOYEE_KEY, pair.employee_id)
  },
  setAccess(token: string) {
    if (DRIVER_OIDC) return
    localStorage.setItem(ACCESS_KEY, token)
  },
  setRefresh(token: string) {
    if (DRIVER_OIDC) return
    localStorage.setItem(REFRESH_KEY, token)
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(EMPLOYEE_KEY)
  },
}

const http = axios.create({
  baseURL: '/api/v1/rewards/driver',
  timeout: 15000,
  withCredentials: DRIVER_OIDC,
})

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (DRIVER_OIDC) {
    if (!['get', 'head', 'options'].includes((config.method ?? 'get').toLowerCase())) {
      for (const [name, value] of Object.entries(driverRequestHeaders())) {
        config.headers.set(name, value)
      }
    }
    return config
  }
  const access = tokenStorage.get()
  if (access) {
    config.headers.set('Authorization', `Bearer ${access}`)
  }
  return config
})

// One in-flight refresh at a time; concurrent 401s wait on the same promise
// so we only burn one refresh token per session-expiry event.
let refreshInFlight: Promise<string> | null = null

async function refreshOnce(): Promise<string> {
  if (refreshInFlight) return refreshInFlight
  const refresh = tokenStorage.getRefresh()
  if (!refresh) throw new Error('No refresh token')
  refreshInFlight = axios
    .post('/api/v1/rewards/driver/auth/refresh', { refresh_token: refresh })
    .then((resp) => {
      const data = resp.data?.data as TokenPair | undefined
      if (!data?.access_token) throw new Error('Refresh response malformed')
      tokenStorage.set(data)
      return data.access_token
    })
    .finally(() => {
      refreshInFlight = null
    })
  return refreshInFlight
}

http.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    if (DRIVER_OIDC) { notifyCarrierRequired(error); return Promise.reject(error) }
    const original = error.config as
      | (InternalAxiosRequestConfig & { __retried?: boolean })
      | undefined
    const isAuthEndpoint = original?.url?.startsWith('/auth/')

    if (
      error.response?.status === 401 &&
      original &&
      !original.__retried &&
      !isAuthEndpoint
    ) {
      try {
        const newAccess = await refreshOnce()
        original.__retried = true
        original.headers.set('Authorization', `Bearer ${newAccess}`)
        return http.request(original as AxiosRequestConfig)
      } catch {
        tokenStorage.clear()
        // Let the calling view handle the 401 — typically it redirects
        // to /login via the router guard on the next navigation.
      }
    }

    return Promise.reject(error)
  },
)

function unwrap<T>(resp: { data: { data: T } }): T {
  return resp.data.data
}

// ---------------------------------------------------------------------------
// Disciplinary "My Record" types + endpoint
// ---------------------------------------------------------------------------

export interface DisciplinaryActionItem {
  id: string
  action_type: string
  offense_key: string
  occurrence_number: number
  event_date: string | null
  action_details_json: Record<string, unknown>
  status: string
  completed_pdf_file_id: string | null
  manager_signed_at: string | null
  driver_signed_at: string | null
  effective_date: string | null
  return_to_duty_date: string | null
}

// Second axios instance for portal-wide endpoints (anything outside
// /rewards/driver). Shares the same auth interceptors so the access JWT is
// attached on each call and a 401 triggers the single-flight refresh.
const portalHttp = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
  withCredentials: DRIVER_OIDC,
})

portalHttp.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (DRIVER_OIDC) {
    if (!['get', 'head', 'options'].includes((config.method ?? 'get').toLowerCase())) {
      for (const [name, value] of Object.entries(driverRequestHeaders())) {
        config.headers.set(name, value)
      }
    }
    return config
  }
  const access = tokenStorage.get()
  if (access) {
    config.headers.set('Authorization', `Bearer ${access}`)
  }
  return config
})

portalHttp.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    if (DRIVER_OIDC) { notifyCarrierRequired(error); return Promise.reject(error) }
    const original = error.config as
      | (InternalAxiosRequestConfig & { __retried?: boolean })
      | undefined

    if (error.response?.status === 401 && original && !original.__retried) {
      try {
        const newAccess = await refreshOnce()
        original.__retried = true
        original.headers.set('Authorization', `Bearer ${newAccess}`)
        return portalHttp.request(original as AxiosRequestConfig)
      } catch {
        tokenStorage.clear()
      }
    }

    return Promise.reject(error)
  },
)

// ---------------------------------------------------------------------------
// Vehicle inspection (DVIR) — the driver's compliance door
// ---------------------------------------------------------------------------
//
// This portal now has two doors onto it. The rewards half is gated on a launch
// decision that has not been made; the inspection half is deliberately
// independent of it, which is why these endpoints live under /driver/dvir and
// not under /rewards/driver. Nothing here reads a rewards flag except
// `DriverSession.rewards_enabled`, which exists precisely so the SPA can hide
// the rewards half rather than the other way round.

export interface DriverSession {
  employee_id: string
  first_name?: string | null
  last_name?: string | null
  assigned_vehicle_count?: number
  rewards_enabled?: boolean
  capabilities?: { driver: boolean; mechanic: boolean; rewards: boolean }
}

export interface DvirVehicle {
  id: string
  unit_number: string | null
  vin: string | null
  year: number | null
  make: string | null
  model: string | null
  vehicle_type: string | null
  trailer_subtype: string | null
}

export interface DvirChecklistItem {
  key: string
  label: string
  result: 'ok' | 'defect' | 'na' | null
  notes: string | null
  photo_path: string | null
}

export interface DvirChecklistCategory {
  name: string
  items: DvirChecklistItem[]
}

export interface DvirOpenDefect {
  id: string
  item_key: string
  item_label: string
  notes: string | null
  severity: string
  status: string
  reported_at: string | null
}

export interface DvirTemplate {
  vehicle_id: string
  vehicle_type: string
  checklist_type: string
  trailer_subtype: string | null
  available_subtypes: { value: string; label: string }[]
  total_items: number
  categories: DvirChecklistCategory[]
  satisfactory_statement: string
  defects_statement: string
  previous_dvir: Record<string, unknown> | null
  requires_review: boolean
  open_defects: DvirOpenDefect[]
  has_safety_critical_open: boolean
}

export const dvirApi = {
  async requestSignInLink(email: string): Promise<void> {
    await portalHttp.post('/driver/dvir/auth/request', { email })
  },

  async session(neutral = false): Promise<DriverSession> {
    return unwrap(await portalHttp.get(neutral ? '/rewards/driver/auth/session' : '/driver/dvir/session'))
  },

  async vehicles(): Promise<DvirVehicle[]> {
    const data = await unwrap<{ vehicles: DvirVehicle[] }>(
      await portalHttp.get('/driver/dvir/vehicles'),
    )
    return data.vehicles
  },

  async template(vehicleId: string, trailerSubtype?: string): Promise<DvirTemplate> {
    return unwrap(
      await portalHttp.get(`/driver/dvir/vehicles/${vehicleId}/template`, {
        params: trailerSubtype ? { trailer_subtype: trailerSubtype } : undefined,
      }),
    )
  },

  async submit(vehicleId: string, form: FormData): Promise<Record<string, unknown>> {
    return unwrap(
      await portalHttp.post(`/driver/dvir/vehicles/${vehicleId}/submit`, form),
    )
  },
}

export interface MechanicJob {
  id: string
  vehicle_id: string
  status: string
  notes?: string | null
  assignee_employee_id: string | null
  assignee_name: string | null
  pm_schedule_id: string | null
  started_at: string | null
  shop_completed_at: string | null
  completed_odometer: number | null
  completed_engine_hours: number | null
  work_items: MechanicWorkItem[]
  filed_record_file_id: string | null
  brake_inspection_not_recorded?: boolean
}

export interface MechanicVehicle {
  id: string
  unit_number: string | null
  vin: string | null
  year: number | null
  make: string | null
  model: string | null
  status: string
  grounded: boolean
  grounded_reasons: Array<{ cause: string; ref?: string }>
  monitoring_paused?: boolean
  monitoring_paused_since?: string | null
  warnings?: string[]
  current_odometer: number | null
  current_engine_hours: number | null
  pm_due_count?: number
  annual_due?: boolean
  open_work_orders?: MechanicJob[]
  work_orders?: MechanicJob[]
  open_defects?: Array<{ id: string; item_label: string; notes: string | null; severity: string; status: string; source: string; photo_url: string | null }>
  pm_schedules?: Array<{ id: string; name: string; next_due_at: string | null; next_due_odometer: number | null; next_due_hours: number | null }>
}

export interface MechanicWorkItem {
  category: string
  work_performed: string
  outcome: string
  performer: string
  brake_inspection: boolean
}

export const mechanicApi = {
  async profile(): Promise<{ first_name: string | null; last_name: string | null }> {
    return (await portalHttp.get('/mechanic/profile')).data
  },
  async defectPhoto(path: string): Promise<string> {
    const response = await portalHttp.get<Blob>(path.replace(/^\/api\/v1/, ''), { responseType: 'blob' })
    return URL.createObjectURL(response.data)
  },
  async requestSignInLink(email: string): Promise<void> {
    await portalHttp.post('/mechanic/auth/request', { email })
  },
  async vehicles(): Promise<MechanicVehicle[]> {
    const response = await portalHttp.get<{ vehicles: MechanicVehicle[] }>('/mechanic/vehicles')
    return response.data.vehicles
  },
  async vehicle(id: string): Promise<MechanicVehicle> {
    return (await portalHttp.get<MechanicVehicle>(`/mechanic/vehicles/${id}`)).data
  },
  async createWorkOrder(vehicleId: string, defectIds: string[]): Promise<MechanicJob> {
    return (await portalHttp.post<MechanicJob>(`/mechanic/vehicles/${vehicleId}/work-orders`, { defect_ids: defectIds })).data
  },
  async workOrder(id: string): Promise<MechanicJob> {
    return (await portalHttp.get<MechanicJob>(`/mechanic/work-orders/${id}`)).data
  },
  async startWorkOrder(id: string): Promise<MechanicJob> {
    return (await portalHttp.post<MechanicJob>(`/mechanic/work-orders/${id}/start`)).data
  },
  async completeWorkOrder(id: string, workItems: MechanicWorkItem[], completedOdometer: number, notes?: string, completedEngineHours?: number | null): Promise<MechanicJob> {
    return (await portalHttp.post<MechanicJob>(`/mechanic/work-orders/${id}/complete`, {
      work_items: workItems, completed_odometer: completedOdometer, notes,
      completed_engine_hours: completedEngineHours ?? null,
    })).data
  },
  async recordMeter(vehicleId: string, kind: 'odometer' | 'engine_hours', value: number): Promise<{ suspect: boolean; suspect_reason: string | null }> {
    return (await portalHttp.post('/mechanic/meter', { vehicle_id: vehicleId, kind, value })).data
  },
}

export const driverApi = {
  async requestMagicLink(email: string): Promise<void> {
    await http.post('/auth/request', { email })
  },

  async myActions(): Promise<DisciplinaryActionItem[]> {
    // /api/v1/disciplinary/me/actions — driver-auth-gated, returns the
    // current driver's signed/in-flight disciplinary record. Response shape
    // is { items: [...] } (not the unwrap APIResponse pattern — this
    // endpoint returns the items dict directly).
    const resp = await portalHttp.get<{ items: DisciplinaryActionItem[] }>(
      '/disciplinary/me/actions',
    )
    return resp.data.items
  },

  async verifyMagicLink(token: string): Promise<TokenPair> {
    return unwrap(await http.post('/auth/verify', { token }))
  },

  async revoke(refresh_token: string): Promise<void> {
    await http.post('/auth/revoke', { refresh_token })
  },

  async me(history = 20): Promise<MeResponse> {
    return unwrap(await http.get('/me', { params: { history } }))
  },

  async orders(limit = 50): Promise<DriverOrder[]> {
    return unwrap(await http.get('/me/orders', { params: { limit } }))
  },

  async catalog(): Promise<Catalog> {
    return unwrap(await http.get('/catalog'))
  },

  async redeem(payload: {
    points: number
    product_id: string
    delivery_email: string
    delivery_name?: string
  }): Promise<DriverOrder> {
    return unwrap(await http.post('/me/redeem', payload))
  },
}
